/**
 * Live thousand-separator formatting for currency inputs (`.js-money`).
 *
 * Design notes — these are the bugs this module exists to not repeat:
 *
 *  - Grouping uses an ASCII space (U+0020), never `toLocaleString`'s narrow
 *    no-break space (U+202F) or non-breaking space (U+00A0). Nette's
 *    `Form::FLOAT` rule strips plain spaces on both the server and the
 *    client (live-form-validation's `float` validator); it does NOT strip
 *    nbsp/narrow-nbsp, so those characters would silently fail validation.
 *  - No `addFilter` and no submit-time stripping. `Form::FLOAT` already
 *    normalizes the value (removing the grouping spaces) before any later
 *    rule runs and before `getValues()`, both server-side and client-side —
 *    formatting here is purely a display concern.
 *  - The `input` handler is delegated on `document` and registered once at
 *    parse time, the same pattern as date-range-filter.js: ajax snippet
 *    redraws and forms-multiplier rows swap the markup underneath it, and a
 *    delegated handler survives that without needing to be re-bound.
 *  - Caret position is restored by digit count, not by character offset —
 *    grouping spaces shift around as digits are typed, so the only stable
 *    anchor is "how many digits are to the left of the caret".
 */
(function (document) {
    "use strict";

    var SELECTOR = ".js-money";

    /**
     * Keeps digits and the first `,` or `.` found in `raw`; groups the
     * integer part by 3 with ASCII spaces. The decimal part (if any) is
     * left ungrouped and keeps its original separator character.
     */
    function format(raw) {
        if (!raw) {
            return "";
        }

        var separatorMatch = raw.match(/[.,]/);
        var separatorIndex = separatorMatch ? separatorMatch.index : -1;

        var integerPart = separatorIndex === -1 ? raw : raw.slice(0, separatorIndex);
        var decimalPart = separatorIndex === -1 ? "" : raw.slice(separatorIndex + 1);

        integerPart = integerPart.replace(/\D/g, "");
        decimalPart = decimalPart.replace(/\D/g, "");

        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

        if (separatorIndex === -1) {
            return integerPart;
        }

        return integerPart + raw.charAt(separatorIndex) + decimalPart;
    }

    /** Number of digits in `value` strictly before `position`. */
    function digitsBefore(value, position) {
        var count = 0;
        for (var i = 0; i < position && i < value.length; i++) {
            if (/\d/.test(value.charAt(i))) {
                count++;
            }
        }
        return count;
    }

    /** The offset in `value` just past the given number of digits. */
    function offsetForDigits(value, digitCount) {
        if (digitCount <= 0) {
            return 0;
        }

        var seen = 0;
        for (var i = 0; i < value.length; i++) {
            if (/\d/.test(value.charAt(i))) {
                seen++;
                if (seen === digitCount) {
                    return i + 1;
                }
            }
        }
        return value.length;
    }

    function reformat(input) {
        var oldValue = input.value;
        var oldCaret = typeof input.selectionStart === "number" ? input.selectionStart : oldValue.length;
        var digitCount = digitsBefore(oldValue, oldCaret);

        var newValue = format(oldValue);
        input.value = newValue;

        if (document.activeElement === input) {
            var newCaret = offsetForDigits(newValue, digitCount);
            try {
                input.setSelectionRange(newCaret, newCaret);
            } catch (e) {
                // Some input types (or browsers mid-redraw) refuse selection
                // range changes; formatting already happened, caret placement
                // is best-effort.
            }
        }
    }

    /**
     * Formats every not-yet-initialized `.js-money` field under `root`.
     * Safe to call repeatedly — the `data-money-init` flag skips fields
     * that were already handled, so a MutationObserver callback covering a
     * whole subtree costs nothing on fields it has already seen.
     */
    function initAll(root) {
        if (!root || typeof root.querySelectorAll !== "function") {
            return;
        }

        var fields = root.querySelectorAll(SELECTOR);
        if (root.nodeType === 1 && root.matches && root.matches(SELECTOR)) {
            fields = Array.prototype.slice.call(fields);
            fields.push(root);
        }

        for (var i = 0; i < fields.length; i++) {
            var field = fields[i];
            if (field.dataset && field.dataset.moneyInit) {
                continue;
            }
            if (field.dataset) {
                field.dataset.moneyInit = "1";
            } else {
                field.setAttribute("data-money-init", "1");
            }

            // Drop an all-zero decimal tail from DB defaults (1000000.00 ->
            // 1000000) but keep a meaningful one (1000000.50 stays as-is).
            var value = field.value;
            if (value && /[.,]0+$/.test(value)) {
                value = value.replace(/[.,]0+$/, "");
            }
            field.value = value;

            field.value = format(field.value);
        }
    }

    document.addEventListener("input", function (event) {
        var target = event.target;
        if (target && target.matches && target.matches(SELECTOR)) {
            reformat(target);
        }
    });

    document.addEventListener("DOMContentLoaded", function () {
        initAll(document);

        var observer = new MutationObserver(function (mutations) {
            for (var i = 0; i < mutations.length; i++) {
                var added = mutations[i].addedNodes;
                for (var j = 0; j < added.length; j++) {
                    var node = added[j];
                    if (node.nodeType === 1) {
                        initAll(node);
                    }
                }
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });
    });
})(document);
